import {
  getRedirectOr,
  getRedirectURL,
} from "@/Providers/session/sessionRedirect";
import careConfig from "@careConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { navigate, usePath } from "raviger";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SessionRecoveryPanel } from "@/Providers/session/SessionRecoveryPanel";
import {
  invalidSession,
  refreshSession,
} from "@/Providers/session/refreshSession";
import {
  isSessionOwner,
  resumeSession,
  setSessionOwner,
  suspendSession,
  useSessionPaused,
} from "@/Providers/session/sessionRecovery";
import { clearSessionQueryCache } from "@/Utils/request/queryClient";
import type { CurrentUserRead } from "@/types/user/user";

import Loading from "@/components/Common/Loading";

import { AuthUserContext } from "@/hooks/useAuthUser";

import { LocalStorageKeys } from "@/common/constants";

import { revokeSessionTokens } from "@/Providers/session/revokeSessionTokens";
import mutate from "@/Utils/request/mutate";
import { userAtom } from "@/atoms/user-atom";
import {
  JwtTokenObtainPair,
  LoginResponse,
  MfaAuthenticationToken,
} from "@/types/auth/auth";
import authApi from "@/types/auth/authApi";
import { TokenData } from "@/types/otp/otp";
import { InitialSessionRecovery } from "./session/InitialSessionRecovery";
import { useSessionUserQuery } from "./session/useSessionUserQuery";

interface Props {
  children: React.ReactNode;
  unauthorized: React.ReactNode;
  otpAuthorized: React.ReactNode;
}

const isMFAResponse = (data: LoginResponse): data is MfaAuthenticationToken => {
  return "temp_token" in data;
};

const isJwtTokenResponse = (
  data: LoginResponse,
): data is JwtTokenObtainPair => {
  return "access" in data && "refresh" in data;
};

export default function AuthUserProvider({
  children,
  unauthorized,
  otpAuthorized,
}: Props) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const paused = useSessionPaused();
  const [user, retainUser] = useState<CurrentUserRead>();
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem(LocalStorageKeys.accessToken),
  );
  const path = usePath();
  const [patientToken, setPatientToken] = useState<TokenData | null>(
    JSON.parse(
      localStorage.getItem(LocalStorageKeys.patientTokenKey) || "null",
    ),
  );

  const {
    data: candidateUser,
    error: currentUserError,
    isLoading,
    refetch: retryIdentity,
  } = useSessionUserQuery(accessToken, user?.id);

  const setUser = useSetAtom(userAtom);
  useEffect(() => {
    if (!candidateUser?.id) return;
    if (user && candidateUser.id !== user.id) {
      suspendSession();
      return;
    }
    if (!user) setSessionOwner(candidateUser.id);
    retainUser(candidateUser);
    setUser(candidateUser);
    resumeSession(candidateUser.id);
  }, [candidateUser, user, setUser]);

  useEffect(() => {
    if (!invalidSession(currentUserError)) return;
    if (user) {
      suspendSession();
      return;
    }
    localStorage.removeItem(LocalStorageKeys.accessToken);
    localStorage.removeItem(LocalStorageKeys.refreshToken);
    setAccessToken(null);
    setUser(undefined);
  }, [currentUserError, user, setUser]);

  const refreshToken = localStorage.getItem(LocalStorageKeys.refreshToken);

  useQuery({
    queryKey: ["user-refresh-token", user?.id],
    queryFn: async () => {
      await refreshSession();
      return true;
    },
    retry: (count, error) => !invalidSession(error) && count < 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    refetchIntervalInBackground: true,
    refetchInterval: careConfig.auth.tokenRefreshInterval,
    // Another tab's rotation suspends this session and re-enables it once the
    // new token is verified. Without a staleTime that re-enable refetches at
    // once, rotating again, which suspends the other tab: two tabs ping-pong
    // refreshes forever and each shows "Session interrupted" in a loop.
    staleTime: careConfig.auth.tokenRefreshInterval,
    enabled: !!refreshToken && !!user && !paused,
  });

  const { mutateAsync: signIn, isPending: isAuthenticating } = useMutation({
    mutationFn: mutate(authApi.login),
    onSuccess: async (data: LoginResponse) => {
      queryClient.invalidateQueries({ queryKey: ["enabled-plugins"] });
      if (isMFAResponse(data)) {
        localStorage.setItem("mfa_temp_token", data.temp_token);
        const redirectURL = getRedirectURL();
        const directURL = path !== "/login" ? window.location.href : null;
        navigate(
          redirectURL
            ? `/2fa?redirect=${redirectURL}`
            : directURL
              ? `/2fa?redirect=${directURL}`
              : "/2fa",
        );
        return;
      }

      if (isJwtTokenResponse(data)) {
        await clearSessionQueryCache();
        setAccessToken(data.access);
        localStorage.setItem(LocalStorageKeys.accessToken, data.access);
        localStorage.setItem(LocalStorageKeys.refreshToken, data.refresh);

        await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
        if (path === "/" || path === "/login") {
          navigate(getRedirectOr("/"));
        }
      }
    },
  });

  const { mutateAsync: verifyMFA, isPending: isVerifyingMFA } = useMutation({
    mutationFn: mutate(authApi.mfa.login),
    onSuccess: async (data: JwtTokenObtainPair) => {
      localStorage.removeItem("mfa_temp_token");
      await clearSessionQueryCache();

      setAccessToken(data.access);
      localStorage.setItem(LocalStorageKeys.accessToken, data.access);
      localStorage.setItem(LocalStorageKeys.refreshToken, data.refresh);

      await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      navigate(getRedirectOr("/"));
    },
  });

  const patientLogin = (tokenData: TokenData, redirectUrl: string) => {
    setPatientToken(tokenData);
    localStorage.setItem(
      LocalStorageKeys.patientTokenKey,
      JSON.stringify(tokenData),
    );
    navigate(redirectUrl);
  };

  const signOut = useCallback(async () => {
    if (user && !window.confirm(t("session_recovery.confirm_signout"))) return;
    suspendSession();
    const accessToken = localStorage.getItem(LocalStorageKeys.accessToken);
    const refreshToken = localStorage.getItem(LocalStorageKeys.refreshToken);

    localStorage.removeItem(LocalStorageKeys.accessToken);
    localStorage.removeItem(LocalStorageKeys.refreshToken);
    localStorage.removeItem(LocalStorageKeys.patientTokenKey);
    setSessionOwner(undefined);
    retainUser(undefined);
    setUser(undefined);
    setAccessToken(null);
    setPatientToken(null);

    await clearSessionQueryCache();

    const redirectURL = getRedirectURL();
    navigate(redirectURL ? `/login?redirect=${redirectURL}` : "/login");
    if (accessToken && refreshToken)
      await revokeSessionTokens(accessToken, refreshToken);
  }, [user, t, setUser]);

  // Handles signout from current tab, if signed out from another tab.
  useEffect(() => {
    const listener = (event: StorageEvent) => {
      if (
        !event.newValue &&
        event.key &&
        [
          LocalStorageKeys.accessToken,
          LocalStorageKeys.refreshToken,
          LocalStorageKeys.patientTokenKey,
        ].includes(event.key)
      ) {
        setSessionOwner(undefined);
        retainUser(undefined);
        setUser(undefined);
        setAccessToken(null);
        setPatientToken(null);
        void clearSessionQueryCache();
      } else if (event.key === LocalStorageKeys.accessToken && event.newValue) {
        // Even a different valid token must prove the same user before reveal.
        if (user) suspendSession();
        setAccessToken(event.newValue);
      }
    };

    addEventListener("storage", listener);

    return () => {
      removeEventListener("storage", listener);
    };
  }, [user, setUser]);

  const recovered = (verified: CurrentUserRead, access: string) => {
    if (verified.id !== user?.id || !isSessionOwner(verified.id)) return;
    queryClient.setQueryData(["currentUser", access], verified);
    retainUser(verified);
    setUser(verified);
    setAccessToken(access);
    resumeSession(verified.id);
  };

  if (
    !user &&
    accessToken &&
    currentUserError &&
    !invalidSession(currentUserError)
  ) {
    return <InitialSessionRecovery retry={() => void retryIdentity()} />;
  }

  if (!user && (isLoading || candidateUser?.id)) {
    return <Loading />;
  }

  return (
    <AuthUserContext.Provider
      value={{
        signIn,
        signOut,
        verifyMFA,
        isAuthenticating,
        isVerifyingMFA,
        user,
        patientLogin,
        patientToken,
      }}
    >
      {user ? (
        <>
          <div
            hidden={paused}
            inert={paused}
            style={{ display: paused ? "none" : "contents" }}
          >
            {children}
          </div>
          {paused && (
            <SessionRecoveryPanel
              user={user}
              onRecovered={recovered}
              onSignOut={signOut}
            />
          )}
        </>
      ) : patientToken?.token ? (
        otpAuthorized
      ) : (
        unauthorized
      )}
    </AuthUserContext.Provider>
  );
}
